import React from "react";
import { createTheme } from "@material-ui/core/styles";
import { BrandsEnum } from "../enums";
import * as ToggleButtons from "./common/ToggleButtonCommonBase";
import * as Helpers from "./common/Helpers";
import * as DatePicker from "./common/DateTimePickerCommonBase";
import { PositionProperty, TextTransformProperty } from "csstype";

// create default theme to get breakpoints
const theme = createTheme();
const colorCard = {};

const variables = {
  common: {
    borderRadius: 0,
  },
  colors: {
    primary: {
      main: "#95572B",
      dark: "#1A1A1A",
      contrastText: "#F8F8F8",
    },
    secondary: {
      main: "#FFFFFF",
      dark: "#000000",
      contrastText: "#FFFFFF",
    },
    error: {
      main: "#EA0029",
    },
    info: {
      main: "#007BFF",
      light: "#D6EAFF",
    },
    text: {
      primary: "#242221",
      secondary: "#1A1A1A",
      hint: "#808890",
    },

    background: "#F8F8F8",
    backgroundHeader: "#EAEAEA",
    border: "#808080",
    hover: "#6C757D",
    placeholder: "#808080",
    transparent: "#00000000",
    divider: "#EAEAEA",
    selectedBackground: "#FFFFFF",
    hoverBorder: "rgba(15,15,15,0.6)",
    hoverButton: "#6C757D",
    stickyFooter: "#DBD3CB",
  },
};
const svgIcons = {
  circleRemove: {
    d: "path('M7 11v2h10v-2H7zm5-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z')",
  },
};

const defaultPathParams: React.SVGProps<SVGPathElement> = {
  stroke: "currentColor",
  strokeWidth: "1.5",
  fill: "none",
};

function getFontFamily(mainFont: string): string {
  return [mainFont ?? "", "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(",");
}

const props = Helpers.PrepareProperties(variables);

// More properties here: https://material-ui.com/customization/default-theme/
export const cupra = {
  themeName: "Cupra",
  brand: BrandsEnum.Cupra,
  colorCard: colorCard,
  compositionBoxWidth: 816,
  containerMaxWidth: 412,
  pageMaxWidth: 1208,
  centred: "0 auto",
  border: "1px solid ",
  shape: {
    borderRadius: variables.common.borderRadius,
  },
  palette: {
    primary: variables.colors.primary,
    secondary: variables.colors.secondary,
    error: variables.colors.error,
    text: variables.colors.text,
    backgroundSecondary: {
      default: variables.colors.backgroundHeader,
      paper: variables.colors.selectedBackground,
    },
    background: {
      default: variables.colors.background,
      paper: variables.colors.selectedBackground,
    },
    divider: variables.colors.divider,
    grey: {
      400: variables.colors.border,
    },
    disabledColor: variables.colors.placeholder,
    info: variables.colors.info,
  },
  typography: {
    fontFamily: getFontFamily("Cupra Book"),
    color: variables.colors.text.primary,
    h1: {
      fontFamily: getFontFamily("Cupra Book"),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.dark,
      [theme.breakpoints.up("md")]: {
        fontSize: "2.5rem",
        lineHeight: "3rem",
      },
      textAlign: "center" as "center",
    },
    h2: {
      fontFamily: getFontFamily("Cupra Book"),
      fontSize: "2rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.dark,
      textAlign: "center" as "center",
      [theme.breakpoints.up("md")]: {
        fontSize: "2rem",
        lineHeight: "2.5rem",
      },
      paddingBottom: theme.spacing(4),
    },
    h3: {
      fontFamily: getFontFamily("Cupra Medium"),
      fontSize: "1.25rem",
      lineHeight: "2.25rem",
      color: variables.colors.primary.dark,
      textDecoration: "underline",
    },
    h4: {
      fontFamily: getFontFamily("Cupra Medium"),
      color: variables.colors.primary.dark,
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
    },
    h5: {
      fontSize: "1.25rem",
      fontFamily: getFontFamily("Cupra Medium"),
      color: variables.colors.text.primary,
    },
    h6: {
      fontFamily: getFontFamily("Cupra Medium"),
      color: variables.colors.secondary.main,
      fontSize: "1rem",
      lineHeight: "1.75rem",
    },
    // helper text
    caption: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
    button: {
      fontSize: "1rem",
      fontWeight: "normal" as "normal",
      lineHeight: "1.5rem",
      minHeight: 48,
    },
    subtitle2: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
      fontWeight: "normal" as "normal",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
    body2: {
      fontSize: "1.2rem",
      lineHeight: "1.5rem",
      color: variables.colors.text.primary,
    },
  },
  radioButton: {
    borderRadius: variables.common.borderRadius,
    border: `1px solid ${variables.colors.border}`,
    activeBorder: `1px solid ${variables.colors.primary.main}`,
    activeBackgroundColor: "#FFFFFF",
    activeFontWeight: 500,
  },
  addOnCard: {
    component: {
      width: 380,
      display: "inline-block",
      marginBottom: "8px",
    },
    card: {
      border: `1px solid ${variables.colors.border}`,
      padding: "25px 34px",
    },
    cardSelected: {
      border: `1px solid ${variables.colors.primary.main}`,
      padding: "25px 34px",
      addText: {
        color: variables.colors.text.secondary,
        fontWeight: 600,
      },
    },
    header: {
      textColor: variables.colors.text.secondary,
    },
    addText: {
      color: variables.colors.text.secondary,
      fontWeight: 600,
    },
    price: {
      color: variables.colors.text.secondary,
      fontWeight: 600,
    },
  },
  checkoutLinks: {
    color: variables.colors.text.secondary,
  },
  appBackgroundColor: variables.colors.background,
  overrides: {
    // Style sheet name. Look for specific component CSS API: https://material-ui.com/api/button/#css
    MuiTypography: {
      root: {
        textTransform: "none" as TextTransformProperty,
      },
    },
    MuiButton: {
      // Name of the rule
      root: {
        // Some CSS
        textTransform: "none" as TextTransformProperty,
      },
      textSecondary: {
        color: variables.colors.text.secondary,
        "&:hover": {
          textDecoration: "underline",
        },
      },
      contained: {
        boxShadow: props.buttons.boxShadow,
        borderRadius: 2,
        "&:hover": {
          boxShadow: props.buttons.boxShadow,
        },
        "&:active": {
          boxShadow: props.buttons.boxShadow,
        },
        "&$focusVisible": {
          boxShadow: props.buttons.boxShadow,
        },
      },
      containedPrimary: {
        backgroundColor: variables.colors.primary.dark,
        fontWeight: 400,
        color: variables.colors.primary.contrastText,
        textDecoration: "none",
        "&:hover": {
          border: "1px solid " + variables.colors.hover,
          color: variables.colors.secondary.contrastText,
          backgroundColor: variables.colors.hover,
          "@media (hover: none)": {
            backgroundColor: variables.colors.hover,
          },
          "& span": {
            textDecoration: "none",
          },
        },
      },
      containedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.dark,
          fontWeight: 600,
          backgroundColor: variables.colors.transparent,
          "& span": {
            textDecoration: "none",
          },
        },
        "&:active": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.dark,
          fontWeight: 600,
          backgroundColor: variables.colors.transparent,
          "& span": {
            textDecoration: "none",
          },
        },
        color: variables.colors.secondary.contrastText,
        backgroundColor: variables.colors.primary.dark,
        border: "1px solid " + variables.colors.primary.dark,
        // this is just to make link styling same as button if link has button classes
        // needed to override link styling by external CSS by making selector more specific
        "a&&": {
          "&:hover": {
            border: "2px solid " + variables.colors.primary.dark,
            color: variables.colors.primary.dark,
            backgroundColor: variables.colors.transparent,
            "& span": {
              textDecoration: "none",
            },
          },
          "&:active": {
            border: "2px solid " + variables.colors.primary.dark,
            color: variables.colors.primary.dark,
            backgroundColor: variables.colors.transparent,
            "& span": {
              textDecoration: "none",
            },
          },
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          border: "1px solid " + variables.colors.primary.dark,
        },
      },
      outlinedSecondary: {
        "&:hover": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          border: "2px solid " + variables.colors.primary.dark,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
        },
        color: variables.colors.primary.dark,
        border: "1px solid " + variables.colors.primary.dark,
      },
      text: {
        "&:hover": {
          color: variables.colors.secondary.dark,
          textDecoration: "underline",
        },
        "&:active": {
          color: variables.colors.text.hint,
        },
        color: variables.colors.text.primary,
      },
      textPrimary: {
        color: variables.colors.primary.dark,
        "&:hover": {
          color: variables.colors.hover,
          backgroundColor: variables.colors.transparent,
        },
        "&:active": {
          backgroundColor: variables.colors.transparent,
        },
      },
      /* Styles applied to the icon element if supplied and `size="medium"`. */
      iconSizeMedium: {
        "& > *:first-child": {
          fontSize: 20,
        },
      },
    },
    MuiRadio: {
      root: {
        color: variables.colors.primary.main,
      },
      colorSecondary: {
        "&.Mui-checked": {
          color: variables.colors.primary.main,
        },
      },
    },
    MuiIconButton: {
      root: {
        color: variables.colors.primary.main,
        "&:hover": {
          color: variables.colors.primary.dark,
        },
      },
      sizeSmall: {
        padding: 0,
      },
    },
    MuiFormLabel: {
      root: {
        color: variables.colors.text.secondary,
        "&$focused": {
          color: variables.colors.border,
        },
        fontWeight: 400,
      },
    },
    MuiInputLabel: {
      formControl: {
        color: variables.colors.text.secondary,
        position: "relative" as PositionProperty,
        transform: "none",
        fontWeight: 400,
        lineHeight: "1.75rem", // safe to use InputLabel + <SvgIcon size='small' />
      },
    },
    MuiOutlinedInput: {
      root: {
        border: "1px solid " + variables.colors.border,
        backgroundColor: variables.colors.selectedBackground,
        "&:hover": {
          borderColor: variables.colors.primary.main,
          "& $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$focused $notchedOutline": {
            borderColor: variables.colors.primary.main,
          },
          "&$error $notchedOutline": {
            borderColor: variables.colors.error.main,
          },
        },
        "&$focused $notchedOutline": {
          borderColor: variables.colors.primary.main,
          borderWidth: 0,
        },
      },
      notchedOutline: {
        borderColor: variables.colors.border,
        borderWidth: 0,
      },
      input: {
        "&::-webkit-input-placeholder": props.inputs.placeholder,
        "&::-moz-placeholder": props.inputs.placeholder, // Firefox 19+
        "&:-ms-input-placeholder": props.inputs.placeholder, // IE 11
        "&::-ms-input-placeholder": props.inputs.placeholder, // Edge
      },
    },
    MuiFormHelperText: {
      root: {
        color: variables.colors.text.hint,
      },
      contained: {
        marginLeft: 0,
      },
    },
    MuiListItem: {
      ...props.listItems,
      button: {
        backgroundColor: variables.colors.background,
        "&:hover": {
          backgroundColor: variables.colors.backgroundHeader,
        },
        "&$selected": {
          backgroundColor: variables.colors.background,
          "&:hover": {
            backgroundColor: variables.colors.backgroundHeader,
          },
        },
      },
    },
    MuiMenuItem: {
      ...props.listItems,
    },
    MuiSelect: {
      select: {
        "&:focus": {
          backgroundColor: variables.colors.transparent,
        },
        // Setting icon with IconComponent property, looses all select styling, so we change SVG path directly to one we need
        "&~ svg": {
          "&> path": {
            d: "path('M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z')",
          },
        },
      },
      icon: {
        fill: variables.colors.primary.main,
      },
    },
    MuiList: {
      padding: {
        paddingTop: 0,
        paddingBottom: 0,
      },
    },
    MuiSvgIcon: {
      fontSizeInherit: {
        fontSize: "56px",
      },
    },
    MuiLink: {
      underlineHover: {
        "a&": {
          // to increase specificy
          color: variables.colors.text.secondary,
          "&:hover": {
            color: variables.colors.hover,
            textDecoration: "underline",
          },
          "&:active": {
            color: variables.colors.hover,
          },
        },
      },
    },
    MuiFormControlLabel: {
      label: {
        color: variables.colors.primary.dark,
        fontWeight: 400,
      },
    },
    MuiToggleButtonGroup: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.root,
        borderRadius: variables.common.borderRadius,
      },
      groupedHorizontal: {
        ...ToggleButtons.CommonBase.MuiToggleButtonGroup.groupedHorizontal,
        ...ToggleButtons.SetMuiToggleButtonGroupBorders({
          ...theme,
          shape: {
            borderRadius: variables.common.borderRadius,
          },
        }),
      },
    },
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: variables.colors.primary.dark,
      },
    },
    MuiToggleButton: {
      root: {
        ...ToggleButtons.CommonBase.MuiToggleButton.root,
        color: variables.colors.text.primary,
        border: "1px solid " + variables.colors.border,
        fontWeight: 400,
        "&$selected": {
          fontFamily: getFontFamily("Cupra Medium"),
          color: variables.colors.primary.dark,
          borderColor: variables.colors.primary.main,
          backgroundColor: variables.colors.transparent,
          "&:hover": {
            fontFamily: getFontFamily("Cupra Medium"),
            borderColor: variables.colors.primary.main,
            backgroundColor: variables.colors.transparent,
          },
        },
        "&:hover": {
          fontFamily: getFontFamily("Cupra Medium"),
          borderColor: variables.colors.primary.main,
          color: variables.colors.primary.dark,
          backgroundColor: variables.colors.transparent,
          [theme.breakpoints.down("sm")]: {
            borderColor: variables.colors.primary.main,
          },
        },
      },
    },
    MuiPickersBasePicker: {
      container: {
        ...DatePicker.CommonBase.MuiPickersBasePicker.container,
      },
    },
    MuiCard: {
      root: {
        width: 385,
      },
    },
  },
  popup: {
    root: {
      fontFamily: getFontFamily("Cupra Book"),
    },
    h4: {
      color: variables.colors.primary.dark,
    },
  },
  uspContainerBlockWrapper: {
    backgroundColor: "#242221",
  },
  uspBlock: {
    header: {
      fontWeight: 500,
      color: "#FFFFFF",
      fontSize: "1.25rem",
      lineHeight: "1.75rem",
      fontFamily: getFontFamily("Cupra Book"),
      [theme.breakpoints.up("md")]: {
        fontSize: "1.5rem",
        lineHeight: "2rem",
      },
    },
    text: {
      fontWeight: 400,
      color: "#FFFFFF",
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  footerBar: {
    backgroundColor: variables.colors.stickyFooter,
    textColor: variables.colors.primary.main,
  },
  footer: {
    bar: {
      color: variables.colors.primary.dark,
      "& h5": {
        color: variables.colors.primary.dark,
      },
      "& h6": {
        color: variables.colors.primary.dark,
      },
      "& svg": {
        color: variables.colors.primary.main,
      },
    },
  },
  priceText: {
    totalPriceColor: variables.colors.primary.dark,
    fontWeight: 400,
  },
  icons: {
    dateTimeIcon: (
      <path
        {...defaultPathParams}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 2.4615,5.7769 h 20.577 V 21.5526 H 2.4615 Z m 20.577,3.4295 H 2.4615 M 17.5513,2.3474 V 5.7769 M 7.9487,2.3474 v 3.4295'
      />
    ),
    infoIcon: (
      <path
        {...defaultPathParams}
        paintOrder='normal'
        strokeLinecap='butt'
        strokeMiterlimit='10'
        d='M 10.75,8.1503401 V 16.34966 m 10,-5.59966 c 0,5.522848 -4.477152,10 -10,10 -5.5228475,0 -10,-4.477152 -10,-10 0,-5.5228475 4.4771525,-10 10,-10 5.522848,0 10,4.4771525 10,10 M 11.402081,5.164 c 0,0.4120491 -0.334032,0.7460809 -0.746081,0.7460809 -0.412049,0 -0.7460809,-0.3340318 -0.7460809,-0.7460809 0,-0.4120491 0.3340319,-0.7460809 0.7460809,-0.7460809 0.412049,0 0.746081,0.3340318 0.746081,0.7460809'
      />
    ),
    crossIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M12 12.2955L17.7045 18L18 17.7045L12.2955 12L18 6.2955L17.7045 6L12 11.7045L6.2955 6L6 6.2955L11.7045 12L6 17.7045L6.2955 18L12 12.2955Z'
          fill='#95572B'
          fill-opacity='0.5'
          stroke='#95572B'
          stroke-width='0.5'
        />
      </svg>
    ),
    checkmarkSelectedOptionIcon: (
      <>
        <path fill='#1A1A1A' stroke='#1A1A1A' d='M-1.2071-.5H48.5v49.7071z' transform='matrix(1.24418 0 0 1.2493 3.00373 1.01704)' />
        <polygon id='Path' points='6 11.17 1.83 7 0.41 8.41 6 14 18 2 16.59 0.59' transform='translate(12 3) scale(0.5)' fill='#FFFFFF' />
      </>
    ),
    checkmarkIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M2 12C2 13.9778 2.58649 15.9112 3.6853 17.5557C4.78411 19.2002 6.3459 20.4819 8.17316 21.2388C10.0004 21.9957 12.0111 22.1937 13.9509 21.8079C15.8907 21.422 17.6725 20.4696 19.0711 19.0711C20.4696 17.6725 21.422 15.8907 21.8078 13.9509C22.1937 12.0111 21.9957 10.0004 21.2388 8.17317C20.4819 6.3459 19.2002 4.78412 17.5557 3.6853C15.9112 2.58649 13.9778 2 12 2C9.34783 2 6.80429 3.05357 4.92893 4.92893C3.05356 6.8043 2 9.34784 2 12Z'
          stroke='#95572B'
        />
        <path
          d='M10.4096 16.3374L6.72289 12.7952L6 13.5904L9.54217 17.0602C9.75904 17.3494 10.0482 17.494 10.4096 17.494C10.7711 17.494 11.1325 17.2771 11.3494 16.988L18 6.57831L17.0602 6L10.4096 16.3374Z'
          fill='#95572B'
        />
      </svg>
    ),
    checkmarkIconCSS: encodeURIComponent(`<svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M2 12C2 13.9778 2.58649 15.9112 3.6853 17.5557C4.78411 19.2002 6.3459 20.4819 8.17316 21.2388C10.0004 21.9957 12.0111 22.1937 13.9509 21.8079C15.8907 21.422 17.6725 20.4696 19.0711 19.0711C20.4696 17.6725 21.422 15.8907 21.8078 13.9509C22.1937 12.0111 21.9957 10.0004 21.2388 8.17317C20.4819 6.3459 19.2002 4.78412 17.5557 3.6853C15.9112 2.58649 13.9778 2 12 2C9.34783 2 6.80429 3.05357 4.92893 4.92893C3.05356 6.8043 2 9.34784 2 12Z"
        stroke="#95572B"
      />
      <path
        d="M10.4096 16.3374L6.72289 12.7952L6 13.5904L9.54217 17.0602C9.75904 17.3494 10.0482 17.494 10.4096 17.494C10.7711 17.494 11.1325 17.2771 11.3494 16.988L18 6.57831L17.0602 6L10.4096 16.3374Z"
        fill="#95572B"
      />
    </svg>`),
    closeIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M4 19.0797L11.0945 11.9852L4 4.89013L4.89073 4L11.9852 11.0945L19.0797 4L19.9704 4.86054L12.8759 11.9852L20 19.1093L19.1099 20L11.9852 12.8753L4.89073 19.9704L4 19.0797Z'
          fill='#1A1A1A'
        />
      </svg>
    ),
    helpIcon: (
      <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M19.2486 5.93503L12.3674 2.64979C11.8346 2.38342 11.2575 2.38342 10.7248 2.64979L4.02109 5.93503C3.39956 6.24579 3 6.86733 3 7.57765V17.3002C3 18.0105 3.39956 18.6764 4.06548 18.9872L10.6804 22.2724C10.9467 22.4056 11.2131 22.45 11.4795 22.45C11.7458 22.45 12.0122 22.4056 12.2786 22.2724L19.2486 18.9872C19.8701 18.6764 20.3141 18.0105 20.3141 17.3002V7.57765C20.2697 6.86733 19.8701 6.24579 19.2486 5.93503ZM19.6038 17.3002C19.6038 17.7441 19.3374 18.1881 18.9378 18.3657L11.9678 21.6509C11.657 21.8285 11.2575 21.8285 10.9467 21.6509L4.33185 18.3657C3.9323 18.1881 3.66593 17.7441 3.66593 17.3002V7.57765C3.66593 7.1337 3.9323 6.68975 4.33185 6.51217L11.0355 3.22693C11.2131 3.13814 11.3907 3.09374 11.5683 3.09374C11.7458 3.09374 11.9234 3.13814 12.101 3.22693L18.9822 6.51217C19.3818 6.68975 19.6482 7.1337 19.6482 7.57765V17.3002H19.6038ZM11.3019 16.8118H11.9678V10.6853H11.3019V16.8118ZM11.657 8.11039C11.3907 8.11039 11.2131 8.28797 11.2131 8.55434C11.2131 8.82071 11.3907 8.99829 11.657 8.99829C11.9234 8.99829 12.101 8.82071 12.101 8.55434C12.101 8.28797 11.879 8.11039 11.657 8.11039Z'
          fill='#95572B'
          stroke='#95572B'
          stroke-width='0.25'
        />
      </svg>
    ),
    backIcon: (
      <path
        {...defaultPathParams}
        stroke={variables.colors.primary.main}
        strokeLinecap='square'
        strokeMiterlimit='10'
        d='m 15,6 -6,6 6,6'
      />
    ),
    addIcon: <path {...defaultPathParams} strokeLinecap='square' strokeMiterlimit='10' d='M 20,12 H 4 m 8,-8 v 16' />,
    statusIndicatorIcon: <circle cx='6' cy='6' r='5' fill={variables.colors.info.main} stroke='white' strokeWidth='2' />,
  },
  messageBox: {
    backgroundColor: "#E3E9F5",
    borderColor: variables.colors.transparent,
    dividerColor: variables.colors.border,
    textAlign: "left",
  },
  header: {
    backgroundColor: variables.colors.background,
  },
  coverageSelect: {
    root: {
      backgroundColor: variables.colors.background,
      CoverageHeaderName: {
        color: variables.colors.text.secondary,
      },
      CoverageItemText: {
        color: variables.colors.text.secondary,
      },
      CoverageHeaderPrice: {
        color: variables.colors.text.secondary,
      },
    },
    selected: {
      backgroundColor: variables.colors.selectedBackground,
      textColor: variables.colors.text.secondary,
    },
    descriptionCard: {
      backgroundColor: variables.colors.selectedBackground,
      borderColor: variables.colors.backgroundHeader,
      textColor: variables.colors.text.primary,
    },
    recommended: {
      backgroundColor: variables.colors.primary.dark,
      borderColor: variables.colors.primary.dark,
    },
    singleRecommended: {
      secondLineColor: {
        backgroundColor: variables.colors.selectedBackground,
      },
    },
  },
  CheckoutPage: {
    priceText: {
      color: variables.colors.primary.dark,
    },
    bottomTotalPriceText: {
      color: variables.colors.primary.dark,
    },
  },
  ConfirmationPage: {
    ConfirmationButtonOutlined: {
      color: variables.colors.primary.dark,
    },
  },
  confirmationPageHeader: {
    color: variables.colors.primary.dark,
  },
  skeletonHeaderText: {
    background: theme.palette.secondary.contrastText,
  },
  paymentOption: {
    label: {
      lineHeight: "1.75rem",
      color: variables.colors.text.secondary,
    },
    selectButton: {
      "& svg": {
        color: variables.colors.primary.dark,
      },
    },
    color: variables.colors.text.secondary,
  },
  stickyPriceButton: {
    backgroundColor: variables.colors.primary.main,
    hoverColor: variables.colors.primary.dark,
    borderColor: variables.colors.primary.main,
    textColor: variables.colors.primary.contrastText,
  },
};

export default createTheme(cupra);
